import React from "react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { CheckCircle, ChevronRight, Circle, Check } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface SetupStep {
  id: string;
  name: string;
  completed: boolean;
  path: string;
}

interface SetupProgressTrackerProps {
  steps: SetupStep[];
  className?: string;
  onDismiss?: () => void;
}

const CTA_LABELS: Record<string, string> = {
  apns: "Configure APNS",
  abm: "Setup Apple Business Manager",
  profiles: "Create Configuration Profiles",
  devices: "Enroll Devices",
};

export const SetupProgressTracker = ({
  steps,
  className,
  onDismiss,
}: SetupProgressTrackerProps) => {
  const [open, setOpen] = React.useState(false);
  const completedSteps = steps.filter((step) => step.completed).length;
  const totalSteps = steps.length;
  const progressPercentage =
    totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  const nextStep = steps.find((step) => !step.completed);
  const allComplete = totalSteps > 0 && !nextStep;
  const ctaLabel = nextStep ? CTA_LABELS[nextStep.id] ?? nextStep.name.replace(/\n/g, " ") : "";

  const handleDismiss = () => {
    setOpen(false);
    onDismiss?.();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`flex items-center gap-2 ${className ?? ""} ${completedSteps > 0 ? "border-success/30" : ""}`}
        >
          <div className="hidden md:flex items-center gap-2">
            <span className="text-sm font-medium">
              Setup Progress: {completedSteps}/{totalSteps}
            </span>
          </div>
          <Progress
            value={progressPercentage}
            className={`w-24 h-2 ${completedSteps > 0 ? "bg-success/10" : ""}`}
            aria-label="Setup progress"
          />
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="p-4 border-b">
          <h3 className="font-medium text-lg">Device Management Setup</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {allComplete
              ? "All setup steps completed! 🎉"
              : `${completedSteps} of ${totalSteps} steps completed (${progressPercentage}%)`}
          </p>
          {progressPercentage > 0 && progressPercentage < 100 && (
            <Progress
              value={progressPercentage}
              className="mt-2 h-2"
              aria-label="Setup progress detail"
            />
          )}
        </div>
        <div className="p-2">
          <ul className="space-y-1">
            {steps.map((step) => (
              <li key={step.id}>
                <Link to={step.path} onClick={() => setOpen(false)}>
                  <Button
                    variant="ghost"
                    className={`w-full justify-start text-left gap-2 h-auto py-3 ${
                      step.completed ? "border-l-2 border-success pl-3" : ""
                    }`}
                  >
                    {step.completed ? (
                      <CheckCircle className="h-5 w-5 text-success shrink-0" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground shrink-0" />
                    )}
                    <div>
                      <span className="font-medium block whitespace-pre-wrap">{step.name}</span>
                      <span className={`text-xs block ${step.completed ? "text-success" : "text-muted-foreground"}`}>
                        {step.completed ? "Completed" : "Pending"}
                      </span>
                    </div>
                    {!step.completed && (
                      <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
                    )}
                    {step.completed && (
                      <Check className="ml-auto h-4 w-4 text-success" />
                    )}
                  </Button>
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="p-4 border-t bg-muted/50">
          {allComplete ? (
            <Button size="sm" className="w-full" onClick={handleDismiss}>
              Dismiss
            </Button>
          ) : nextStep ? (
            <Button asChild size="sm" className="w-full">
              <Link
                to={nextStep.path}
                className="flex items-center justify-center"
                onClick={() => setOpen(false)}
              >
                <span className="text-sm">{ctaLabel}</span>
                <ChevronRight className="ml-1 h-3 w-3" />
              </Link>
            </Button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
};
